import React, { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

const MIN_PANEL_HEIGHT = 120; // px

const Handle = styled(Box)(({ theme }: { theme: Theme }) => ({
  width: '36px',
  height: '6px',
  borderRadius: '4px',
  border: `1px solid ${theme.room.tileBackground}`,
  backgroundColor: '#fff',
}));

interface ResizeDividerProps {
  onRatioChange: (ratio: number) => void;
  onDragEnd: (ratio: number) => void;
}

export const ResizeDivider = ({ onRatioChange, onDragEnd }: ResizeDividerProps) => {
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const ratioRef = useRef(0);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    movedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) {
        return;
      }

      const rect = e.currentTarget.parentElement?.getBoundingClientRect();
      if (!rect || rect.height === 0) {
        return;
      }

      const y = e.clientY - rect.top;
      const minRatio = MIN_PANEL_HEIGHT / rect.height;
      const maxRatio = 1 - minRatio;
      const ratio = Math.min(maxRatio, Math.max(minRatio, y / rect.height));

      movedRef.current = true;
      ratioRef.current = ratio;
      onRatioChange(ratio);
    },
    [onRatioChange]
  );

  const handlePointerUp = useCallback(() => {
    if (draggingRef.current) {
      draggingRef.current = false;
      if (movedRef.current) {
        onDragEnd(ratioRef.current);
      }
    }
  }, [onDragEnd]);

  return (
    <Box
      data-testid="resize-divider"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: 'absolute',
        top: 'calc(var(--panels-split-ratio) * 100%)',
        left: 0,
        right: 0,
        height: '8px',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'ns-resize',
        zIndex: 2,
        touchAction: 'none',
      }}
    >
      <Handle data-testid="resize-pill" />
    </Box>
  );
};
